import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useCallback } from "react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAppForm } from "@/components/ui/form";
import { cn } from "@/components/ui/lib/utils";
import { Separator } from "@/components/ui/separator";
import { authClient, signIn } from "@/lib/auth-client";
import * as m from "@/paraglide/messages";

import { GoogleIcon } from "../icons/google";
import { PreserverSearchLink } from "../preserver-search-link";

type SignInFormValues = {
	email: string;
	password: string;
};

const defaultValues: SignInFormValues = {
	email: "",
	password: "",
};

type LoginFormProps = React.ComponentProps<"div"> & {
	redirectTo?: string;
};

const signInSchema = z.object({
	email: z.email(m.auth_emailInvalid()),
	password: z.string().min(1, m.auth_passwordRequired()),
});

export function LoginForm({ className, redirectTo = "/", ...props }: LoginFormProps) {
	const googleSignInMutation = useMutation({
		mutationKey: ["auth", "sign-in-google"],
		mutationFn: async () => {
			const response = await authClient.signIn.social(
				{
					provider: "google",
					callbackURL: redirectTo,
				},
				{ throw: true },
			);

			return response;
		},
		meta: {
			error: { message: m.auth_googleSignInFailed() },
		},
	});

	const signInMutation = useMutation({
		mutationKey: ["auth", "sign-in"],
		mutationFn: async (values: SignInFormValues) => {
			const result = await signIn.email(
				{
					email: values.email,
					password: values.password,
					callbackURL: redirectTo,
				},
				{ throw: true },
			);

			return result;
		},
		meta: {
			success: { message: m.auth_signInSuccess() },
			error: { message: m.auth_signInFailed() },
		},
		onSuccess: () => {
			window.location.href = redirectTo;
		},
	});

	const form = useAppForm({
		validators: { onChange: signInSchema },
		defaultValues,
		onSubmit: ({ value }) => {
			signInMutation.mutate(value);
		},
	});

	const handleSubmit = useCallback(
		(e: React.SubmitEvent) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		[form],
	);

	return (
		<div className={cn("flex flex-col gap-6", className)} {...props}>
			<Card>
				<CardHeader>
					<CardTitle>{m.auth_loginTitle()}</CardTitle>
					<CardDescription>{m.auth_loginDesc()}</CardDescription>
				</CardHeader>
				<CardContent>
					<form.AppForm>
						<form className="space-y-4" onSubmit={handleSubmit}>
							<form.AppField name="email">
								{(field) => (
									<field.TextField
										label={m.auth_email()}
										type="email"
										placeholder="m@example.com"
										autoComplete="email"
										required
									/>
								)}
							</form.AppField>
							<form.AppField name="password">
								{(field) => (
									<field.PasswordField
										label={
											<span className="flex w-full items-center justify-between">
												{m.auth_password()}
												<Link
													to="/forgot-password"
													className="text-sm font-normal underline-offset-4 hover:underline"
												>
													{m.auth_forgotPassword()}
												</Link>
											</span>
										}
										placeholder={m.auth_passwordPlaceholder()}
										autoComplete="current-password"
										required
									/>
								)}
							</form.AppField>
							<div className="flex flex-col gap-2">
								<form.SubmitButton
									className="w-full"
									disabled={signInMutation.isPending}
								>
									{signInMutation.isPending ? m.auth_signingIn() : m.auth_login()}
								</form.SubmitButton>
								<div className="relative">
									<div className="absolute inset-0 flex items-center">
										<Separator className="w-full" />
									</div>
									<div className="relative flex justify-center text-xs uppercase">
										<span className="bg-card text-muted-foreground px-2">
											{m.auth_orContinueWith()}
										</span>
									</div>
								</div>
								<Button
									variant="outline"
									type="button"
									className="w-full"
									disabled={googleSignInMutation.isPending}
									onClick={() => googleSignInMutation.mutate()}
								>
									<GoogleIcon className="mr-2 h-4 w-4" />
									{googleSignInMutation.isPending
										? m.auth_redirecting()
										: m.auth_continueWithGoogle()}
								</Button>
							</div>
							<PreserverSearchLink
								to="/magic-link"
								className="text-muted-foreground block text-center text-sm underline underline-offset-4"
							>
								{m.auth_signInWithMagicLink()}
							</PreserverSearchLink>
							<p className="text-muted-foreground text-center text-sm">
								{m.auth_noAccount()}{" "}
								<PreserverSearchLink
									to="/sign-up"
									className="underline underline-offset-4"
								>
									{m.auth_signUp()}
								</PreserverSearchLink>
							</p>
						</form>
					</form.AppForm>
				</CardContent>
			</Card>
		</div>
	);
}
